import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Checkbox, List, ListItem, ListItemText, ListItemIcon, Box, Typography, CircularProgress, Chip, TableSortLabel, InputAdornment, Menu, Card, CardContent, Avatar, Divider, Alert
} from '@mui/material';
import { Edit, Delete, Add, LockReset, Person, Email, Work, LocationOn, Search, FilterList } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import apiService from '../services/api';
import { auth } from '../firebase/config';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  active: string;
  loginEnabled: string;
}

interface PaginatedUsers {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
}

interface Location {
  location_id: number;
  lab_code: string;
  labName: string;
  location_type: string;
  institution_id: number | null;
}

interface Role {
  id: number;
  name: string;
  description: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'accessioner', active: 'yes', loginEnabled: 'yes' });
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [expandedRows, setExpandedRows] = useState<{ [userId: string]: boolean }>({});
  const [sortBy, setSortBy] = useState<'name' | 'role' | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [locationFilterAnchorEl, setLocationFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [resetPwDialogOpen, setResetPwDialogOpen] = useState(false);
  const [resetPwUser, setResetPwUser] = useState<User | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetPwLoading, setResetPwLoading] = useState(false);
  const [resetPwError, setResetPwError] = useState<string | null>(null);
  const [resetPwSuccess, setResetPwSuccess] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchUsers = async (pageNum = 0, pageSize = 10) => {
    try {
      const data: PaginatedUsers = await apiService.getUsers(pageNum + 1, pageSize);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      setUsers([]);
      setTotal(0);
    }
  };

  const fetchAllLocations = async () => {
    setLocationsLoading(true);
    try {
      const data = await apiService.getAllLocations();
      console.log('All locations fetched:', data);
      setAllLocations(data || []); // getAllLocations returns a flat array
    } catch (error) {
      console.error('Error fetching locations:', error);
      setAllLocations([]);
    }
    setLocationsLoading(false);
  };

  const fetchAllRoles = async () => {
    try {
      const data = await apiService.getRoles(1, 1000);
      setAllRoles(data.roles || []);
    } catch {
      setAllRoles([]);
    }
  };

  useEffect(() => {
    fetchUsers(page, rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchAllLocations();
    fetchAllRoles();
  }, []);

  useEffect(() => {
    if (openDialog) {
      if (editingUser && (editingUser as any).locations) {
        console.log('User locations:', (editingUser as any).locations);
        // Handle different possible location data structures
        const locationIds = (editingUser as any).locations.map((l: any) => {
          // Try different possible property names
          return l.location_id || l.id || l.locationId;
        }).filter((id: any) => id != null);
        console.log('Mapped location IDs:', locationIds);
        setSelectedLocations(locationIds);
      } else {
        setSelectedLocations([]);
      }
      if (editingUser && (editingUser as any).roles) {
        console.log('User roles:', (editingUser as any).roles);
        const roleIds = (editingUser as any).roles.map((r: any) => r.id).filter((id: any) => id != null);
        console.log('Mapped role IDs:', roleIds);
        setSelectedRoleIds(roleIds);
      } else {
        setSelectedRoleIds([]);
      }
    }
  }, [openDialog, editingUser]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active || 'yes',
      loginEnabled: user.loginEnabled || 'yes',
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this user?')) {
      await apiService.deleteUser(id.toString());
      fetchUsers(page, rowsPerPage);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setForm({ name: '', email: '', role: 'accessioner', active: 'yes', loginEnabled: 'yes' });
  };

  const handleLocationToggle = (id: number, isHQ: boolean, labSiteIds: number[] = []) => {
    setSelectedLocations(prev => {
      if (isHQ) {
        const allSelected = [id, ...labSiteIds].every(lid => prev.includes(lid));
        if (allSelected) {
          return prev.filter(lid => ![id, ...labSiteIds].includes(lid));
        } else {
          return Array.from(new Set([...prev, id, ...labSiteIds]));
        }
      } else {
        if (prev.includes(id)) {
          return prev.filter(lid => lid !== id);
        } else {
          return [...prev, id];
        }
      }
    });
  };



  const handleDialogSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    
    // Validate that at least one location is selected
    if (selectedLocations.length === 0) {
      setSaveError('At least one location must be selected');
      setSaveLoading(false);
      return;
    }
    
    try {
      if (editingUser) {
        // Update existing user
        const payload = { ...form, locations: selectedLocations, roles: selectedRoleIds };
        await apiService.updateUser(editingUser.id.toString(), payload);
      } else {
        // Create new user via Firebase Auth
        const password = 'changeme123'; // Default password for new users
        
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, form.email, password);
        const firebaseUser = userCredential.user;
        
        // Update the user's display name in Firebase Auth
        await updateProfile(firebaseUser, {
          displayName: form.name
        });
        
        // The Firebase Auth trigger (createFirestoreUser) will automatically create the PostgreSQL record
        // We need to wait a moment for the trigger to complete, then update the user with additional data
        setTimeout(async () => {
          try {
            // Update the user with roles and locations
            const payload = {
              uid: firebaseUser.uid,
              name: form.name,
              active: form.active,
              loginEnabled: form.loginEnabled,
              roles: selectedRoleIds,
              locations: selectedLocations
            };
            
            await apiService.updateUser(firebaseUser.uid, payload);
          } catch (updateError) {
            console.error('Error updating user with roles/locations:', updateError);
          }
        }, 2000); // Wait 2 seconds for the trigger to complete
      }
      
      setOpenDialog(false);
      setEditingUser(null);
      setForm({ name: '', email: '', role: 'accessioner', active: 'yes', loginEnabled: 'yes' });
      fetchUsers(page, rowsPerPage);
    } catch (error) {
      console.error('Error saving user:', error);
      setSaveError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setSaveLoading(false);
    }
  };

  // Group locations by HQ
  const hqs = allLocations.filter(l => l.location_type === 'HQ');
  const labSites = allLocations.filter(l => l.location_type === 'Lab Site');
  const labSitesByHQ: { [hqId: number]: Location[] } = {};
  labSites.forEach(site => {
    if (!site.institution_id) return;
    if (!labSitesByHQ[site.institution_id]) labSitesByHQ[site.institution_id] = [];
    labSitesByHQ[site.institution_id].push(site);
  });

  // For the location selection, show only Lab Sites (non-HQ locations)
  console.log('All locations with types:', allLocations.map(l => ({ id: l.location_id, name: l.labName, type: l.location_type })));
  const availableLocations = allLocations.filter(l => l.location_type === 'Lab Site');
  console.log('Available locations (Lab Sites only):', availableLocations);

  const allLabSitesChecked = (hqId: number) =>
    labSitesByHQ[hqId] && labSitesByHQ[hqId].length > 0
      ? labSitesByHQ[hqId].every(site => selectedLocations.includes(site.location_id))
      : false;

  const handleExpandClick = (userId: string) => {
    setExpandedRows(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleSort = (column: 'name' | 'role') => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const allLocationOptions = Array.from(
    users.reduce((acc, user) => {
      if (Array.isArray((user as any).locations)) {
        (user as any).locations.forEach((loc: any) => acc.set(loc.location_id, loc.labName));
      }
      return acc;
    }, new Map<number, string>())
  ).map(([id, name]) => ({ id, name }));

  const handleLocationFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setLocationFilterAnchorEl(event.currentTarget);
  };
  const handleLocationFilterClose = () => {
    setLocationFilterAnchorEl(null);
  };
  const handleLocationFilterChange = (id: number) => {
    setSelectedLocationIds(prev =>
      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
    );
  };
  const handleClearLocationFilter = () => {
    setSelectedLocationIds([]);
  };

  const filteredUsers = users.filter(user => {
    const q = search.toLowerCase();
    const matchesSearch =
      user.name.toLowerCase().includes(q) ||
      user.email.toLowerCase().includes(q);
    let matchesLocation = true;
    if (selectedLocationIds.length > 0) {
      const userLocIds = Array.isArray((user as any).locations)
        ? (user as any).locations.map((l: any) => l.location_id)
        : [];
      matchesLocation = selectedLocationIds.some(id => userLocIds.includes(id));
    }
    return matchesSearch && matchesLocation;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortBy === 'name') {
      return sortDirection === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else if (sortBy === 'role') {
      // Sort by first role name (if any)
      const aRole = Array.isArray((a as any).roles) && (a as any).roles.length > 0 ? (a as any).roles[0].name : '';
      const bRole = Array.isArray((b as any).roles) && (b as any).roles.length > 0 ? (b as any).roles[0].name : '';
      return sortDirection === 'asc'
        ? aRole.localeCompare(bRole)
        : bRole.localeCompare(aRole);
    }
    return 0;
  });

  const handleOpenResetPwDialog = (user: User) => {
    setResetPwUser(user);
    setResetPw('');
    setResetPwError(null);
    setResetPwSuccess(null);
    setResetPwDialogOpen(true);
  };
  const handleCloseResetPwDialog = () => {
    setResetPwDialogOpen(false);
    setResetPwUser(null);
    setResetPw('');
    setResetPwError(null);
    setResetPwSuccess(null);
  };
  const handleResetPassword = async () => {
    if (!resetPwUser || !resetPw) return;
    setResetPwLoading(true);
    setResetPwError(null);
    setResetPwSuccess(null);
    try {
      await apiService.resetUserPassword(resetPwUser.id.toString(), resetPw);
      setResetPwSuccess('Password reset successfully');
      setTimeout(() => {
        setResetPwDialogOpen(false);
        setResetPwUser(null);
        setResetPw('');
        setResetPwSuccess(null);
      }, 2000);
    } catch (error: any) {
      setResetPwError(error.message || 'Failed to reset password');
    }
    setResetPwLoading(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: '#1a237e', fontWeight: 600 }}>
        Users Management
      </Typography>
      
      {/* Header with Actions and Search */}
      <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => setOpenDialog(true)}
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              Add User
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#e9ecef',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                    }
                  }
                }}
                sx={{ 
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  }
                }}
              />
              
              <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                {filteredUsers.length} of {total} users
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 18 }} />
                      <span>Name</span>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Email sx={{ fontSize: 18 }} />
                    <span>Email</span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <TableSortLabel
                    active={sortBy === 'role'}
                    direction={sortBy === 'role' ? sortDirection : 'asc'}
                    onClick={() => handleSort('role')}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Work sx={{ fontSize: 18 }} />
                      <span>Role</span>
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOn sx={{ fontSize: 18 }} />
                    <span>Locations</span>
                    <IconButton 
                      size="small" 
                      onClick={handleLocationFilterClick} 
                      color={selectedLocationIds.length > 0 ? 'primary' : 'default'}
                      sx={{ ml: 1 }}
                    >
                      <FilterList />
                    </IconButton>
                    <Menu
                      anchorEl={locationFilterAnchorEl}
                      open={Boolean(locationFilterAnchorEl)}
                      onClose={handleLocationFilterClose}
                      MenuListProps={{ dense: true }}
                    >
                      <MenuItem disabled>Filter by Location</MenuItem>
                      {allLocationOptions.map(loc => (
                        <MenuItem key={loc.id} onClick={() => handleLocationFilterChange(loc.id)}>
                          <Checkbox checked={selectedLocationIds.includes(loc.id)} />
                          <ListItemText primary={loc.name} />
                        </MenuItem>
                      ))}
                      <MenuItem onClick={handleClearLocationFilter} disabled={selectedLocationIds.length === 0}>
                        Clear Filter
                      </MenuItem>
                    </Menu>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Person sx={{ fontSize: 18 }} />
                    <span>Created At</span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <span>Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow 
                  key={user.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: '#f8f9fa',
                      transition: 'background-color 0.2s ease'
                    }
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2', fontSize: '0.875rem' }}>
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {Array.isArray((user as any).roles) && (user as any).roles.length > 0 ? (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {(user as any).roles.map((r: any, idx: number) => {
                          const roleColors: Record<string, 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
                            'Lab Site Admin': 'primary',
                            'Molecular / Ancillary User': 'info',
                            'Autopsy Technician': 'success',
                            'HQ Admin': 'secondary',
                            'Accessioner': 'warning',
                            'Pathologist': 'error',
                          };
                          const color = roleColors[r.name] || 'default';
                          return (
                            <Chip 
                              key={r.id} 
                              label={r.name} 
                              color={color as any} 
                              size="small"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                        No roles assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {Array.isArray((user as any).locations) && (user as any).locations.length > 0 ? (() => {
                      const locs = (user as any).locations;
                      const showCount = 3;
                      const isExpanded = expandedRows[user.id];
                      if (locs.length <= showCount || isExpanded) {
                        return (
                          <Box>
                            {locs.map((l: any, idx: number) => (
                              <Typography key={l.location_id} variant="body2" sx={{ mb: 0.5 }}>
                                {l.labName}
                              </Typography>
                            ))}
                            {locs.length > showCount && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  cursor: 'pointer', 
                                  color: '#1976d2',
                                  textDecoration: 'underline',
                                  '&:hover': { color: '#1565c0' }
                                }} 
                                onClick={() => handleExpandClick(user.id)}
                              >
                                Show less
                              </Typography>
                            )}
                          </Box>
                        );
                      } else {
                        return (
                          <Box>
                            <Typography variant="body2">
                              {locs.slice(0, showCount).map((l: any) => l.labName).join(', ')}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                cursor: 'pointer', 
                                color: '#1976d2',
                                textDecoration: 'underline',
                                '&:hover': { color: '#1565c0' }
                              }} 
                              onClick={() => handleExpandClick(user.id)}
                            >
                              ...more
                            </Typography>
                          </Box>
                        );
                      }
                    })() : (
                      <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                        No locations assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <IconButton 
                        onClick={() => handleEdit(user)}
                        sx={{ 
                          color: '#1976d2',
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                        }}
                        title="Edit User"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(user.id)}
                        sx={{ 
                          color: '#d32f2f',
                          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' }
                        }}
                        title="Delete User"
                      >
                        <Delete />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleOpenResetPwDialog(user)} 
                        sx={{ 
                          color: '#ed6c02',
                          '&:hover': { backgroundColor: 'rgba(237, 108, 2, 0.1)' }
                        }}
                        title="Reset Password"
                      >
                        <LockReset />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {sortedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      {search || selectedLocationIds.length > 0 ? 'No users found matching your criteria.' : 'No users found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Divider />
        
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              color: '#666',
            }
          }}
        />
      </Card>

      {/* Dialog for Add/Edit User */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        {saveError && (
          <Box sx={{ px: 3, py: 1 }}>
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Box>
        )}
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            variant="standard"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="standard"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
          <TextField
            select
            margin="dense"
            label="Active"
            fullWidth
            value={form.active}
            onChange={e => setForm(f => ({ ...f, active: e.target.value }))}
            sx={{ mt: 1 }}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
          <TextField
            select
            margin="dense"
            label="Login Enabled"
            fullWidth
            value={form.loginEnabled}
            onChange={e => setForm(f => ({ ...f, loginEnabled: e.target.value }))}
            sx={{ mt: 1 }}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
          <Autocomplete
            multiple
            options={allRoles}
            disableCloseOnSelect
            getOptionLabel={option => option.name}
            value={allRoles.filter(r => selectedRoleIds.includes(r.id))}
            onChange={(_, value) => setSelectedRoleIds(value.map(r => r.id))}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name}
              </li>
            )}
            renderInput={params => (
              <TextField {...params} label="Roles" placeholder="Select roles" margin="dense" fullWidth />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
                     <Box sx={{ mt: 2 }}>
             <Typography variant="subtitle1" color={selectedLocations.length === 0 ? 'error' : 'inherit'}>
               Assign Locations {selectedLocations.length === 0 && '*'}
             </Typography>
             {selectedLocations.length === 0 && (
               <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                 At least one location must be selected
               </Typography>
             )}
             {locationsLoading ? (
               <CircularProgress size={24} />
             ) : (
               <List dense>
                 {availableLocations.map(location => (
                   <ListItem key={location.location_id}>
                     <ListItemIcon>
                       <Checkbox
                         edge="start"
                         checked={selectedLocations.includes(location.location_id)}
                         tabIndex={-1}
                         disableRipple
                         onChange={() => handleLocationToggle(location.location_id, false)}
                       />
                     </ListItemIcon>
                     <ListItemText primary={location.labName} secondary={location.location_type} />
                   </ListItem>
                 ))}
               </List>
             )}
           </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saveLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDialogSave} 
            variant="contained" 
            disabled={saveLoading || selectedLocations.length === 0}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Reset Password */}
      <Dialog open={resetPwDialogOpen} onClose={handleCloseResetPwDialog}>
        <DialogTitle>Reset Password for {resetPwUser?.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={resetPw}
            onChange={e => setResetPw(e.target.value)}
            margin="normal"
            autoFocus
          />
          {resetPwError && <Typography color="error" variant="body2">{resetPwError}</Typography>}
          {resetPwSuccess && <Typography color="success.main" variant="body2">{resetPwSuccess}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPwDialog}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={resetPwLoading || !resetPw}>
            {resetPwLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 